    import React, {useEffect, useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import {AppError, Loading, WrapperComponent} from '../../../components';
import _ from 'lodash';
import {useProductsQuery} from "../../../queries/products";
import {useCartQuery, useCartMineQuery, useCreateCartMutation, useUpdateCartMutation} from "../../../queries/carts";
import {useLocalStorage} from "../../../helpers/localStorageHelper";
import {useRouter} from 'next/router';
import {useUser} from "@auth0/nextjs-auth0/client";
import {
    prepareCartOrOrderForPut,
    prepareCartOrOrderForUi,
    mapProductToItemLine,
    AGREEMENT_CODE_FIELD_NAME, parseCartInfoFromLanding
} from "../../../helpers/storeHelpers";
import {useRole} from "../../../helpers/RoleProvider";
import UnAuthorized from "../../../components/UnAuthorized";
import {toast} from "react-hot-toast";
import Configurator from "../../../components/CheckoutForm/Configurator";
import {useMobileAppView} from "../../../helpers/MobileAppViewProvider";
import {useUsersMeQuery} from "../../../queries/users";


const Index = () => {
    const router = useRouter();
    const {user, error: errorUser, isError: isErrorUser, isLoading: isLoadingUser} = useUser();
    const {role} = useRole();
    const {query} = router;
    const {isMobileAppView} = useMobileAppView();

    const [cartForUi, setCartForUi] = useState(null)
    const [firstCartLoaded, setFirstCartLoaded] = useState(false)
    const [skipUpdate, setSkipUpdate] = useState(true) // Used to skip the first update when cart was just loaded
    const [initialProduct, setInitialProduct] = useState(null)

    const [cartIdStorage, setCartIdStorage] = useLocalStorage('cartId')
    const cartIdFromQuery = query?.cartId
    const srcFromQuery = query?.src
    const isLoggedUserFromQuery = query?.isLoggedUser?.toLowerCase() === 'true'
    const initialProductsFromQuery = query?.prods
    const partnerFromQuery = query?.partner
    const cartId = cartIdFromQuery ?? cartIdStorage

    const userProfileQueryEnabled = !!(user && role && role.toLowerCase() === "customer");
    const cartMineQueryEnabled = userProfileQueryEnabled;
    const cartQueryEnabled = !!(
        !initialProductsFromQuery
        && !!cartIdFromQuery  // cartIdFromQuery has precedence and ignores cartMineFromQuery
        || (!!cartIdStorage && !cartMineQueryEnabled))

    const {
        data: products,
        isIdle: isIdleProducts,
        isLoading: isLoadingProducts,
        isError: isErrorProducts,
        error: errorProducts,
    } = useProductsQuery();

    const {
        data: cartFromApi,
        isIdle: isIdleCartFromApi,
        isLoading: isLoadingCartFromApi,
        isError: isErrorCartFromApi,
        error: errorCartFromApi,
    } = useCartQuery({cartId: cartId, queryOptions: {enabled: cartQueryEnabled}});

    const {
        data: cartMineFromApi,
        isIdle: isIdleCartMineFromApi,
        isLoading: isLoadingCartMineFromApi,
        isError: isErrorCartMineFromApi,
        error: errorCartMineFromApi,
        isStale: isStaleCartMineFromApi,
        isFetching: isFetchingCartMineFromApi,
        isPreviousData: isPreviousDataCartMineFromApi,
    } = useCartMineQuery({queryOptions: {enabled: cartMineQueryEnabled}});

    const {
        data: userProfile,
        isIdle: isIdleUserProfile,
        isLoading: isLoadingUserProfile,
        isError: isErrorUserProfile,
        error:errorUserProfile
    } = useUsersMeQuery({queryOptions: {enabled: userProfileQueryEnabled}})


    const createCartMutation = useCreateCartMutation()
    const updateCartMutation = useUpdateCartMutation()

    const dependenciesAreReady = !isLoadingUser && !isErrorUser && !isLoadingProducts && !isErrorProducts
        && (!cartQueryEnabled || (!isLoadingCartFromApi && !isErrorCartFromApi))
        && (!cartMineQueryEnabled || (!isLoadingCartMineFromApi && !isErrorCartMineFromApi))
        && (!userProfileQueryEnabled || (!isLoadingUserProfile && !isErrorUserProfile))

    const agreeementPartnershipEnabled = userProfileQueryEnabled && userProfile?.center?.is_partner &&
        userProfile?.center?.partnership_agreement?.enabled
    /*-- USE-EFFECTS ------------------------------------------------------------------------*/

    useEffect(() => {
        if (cartForUi && cartId) {
            //console.log("CartForUi", cartForUi)
            // Skipping first update cause cart was just loaded - this also fixed a concurrency bug so don't remove
            if (!skipUpdate) {
                saveCart()
            }
            setSkipUpdate(false)
        }
    }, [cartForUi])

    useEffect(() => {
        if (cartIdFromQuery) {
            setCartIdStorage(cartIdFromQuery)
        }
    }, [cartIdFromQuery])

    useEffect(() => {
        if (!cartMineQueryEnabled && isErrorCartFromApi && errorCartFromApi?.response?.status === 401) {
            // user is NOT logged in (cartMineQuery is not enabled) but cart from api with an ID has returned a 401:
            // this happens when a user logs out and the local storage still has the old cartId (the cart of the preceding logged-in user)
            createNewCart()
        } else if (isErrorCartFromApi && errorCartFromApi?.response?.status === 404) {
            // Fallback for some side cases
            createNewCart()
        }
    }, [isErrorCartFromApi, errorCartFromApi, cartMineQueryEnabled])

    useEffect(() => {
        if (isErrorCartFromApi && errorCartFromApi) {
            console.log(errorCartFromApi)
            if (errorCartFromApi?.response?.status === 404) {
                console.log("RECREATE!")
            }
        }
    }, [isErrorCartFromApi, errorCartFromApi])

    // useEffect(() => {
    //     if (dependenciesAreReady && cartIdFromQuery && products) {
    //         setInitialProduct(products.find(p => (p.name).toLowerCase() === cartIdFromQuery.toLowerCase()))
    //     }
    // }, [cartIdFromQuery, dependenciesAreReady])

    useEffect(() => {
            if (dependenciesAreReady && !!partnerFromQuery && !user) {
                // If a partner is present in query string and the user is null (not logged in), redirect the user to the login page
                router.push(`/api/auth/login?returnTo=${router.asPath}`)
            } else if (dependenciesAreReady && !isFetchingCartMineFromApi && !firstCartLoaded) {
                if (!user && isLoggedUserFromQuery) {
                    router.push(`/api/auth/login?returnTo=${router.asPath}`)
                } else {
                    let finalCart
                    let productsOverride = null  // set this to override - if not set products will be taken from
                    // the cart itself (it is necessary for carts from APIs since they might contain private products)
                    if (initialProductsFromQuery) {  // Has priority over cartMine (used for landing page integration)
                        if (cartMineFromApi && (!cartIdStorage || cartMineFromApi.id !== cartIdStorage)) {
                            setCartIdStorage(cartMineFromApi.id)
                        }
                        const initialProductsObj = JSON.parse(initialProductsFromQuery)
                        finalCart = parseCartInfoFromLanding(initialProductsObj, products)
                        productsOverride = products;
                        // setCartForUi(prepareCartOrOrderForUi(finalCart, products))
                    } else if (cartIdFromQuery) {  // Has priority over cartMine (used for landing page integration)
                        setCartIdStorage(cartIdFromQuery)
                        if (cartMineQueryEnabled) { // user is logged in thus I need to save the cart for him with the login mutation
                            router.push("/landing/login?action=backToConfig")
                        } else {
                            console.log("Setting cart via id from query string")
                            finalCart = cartFromApi
                        }
                        console.log("Setting cart via id from query string")
                        finalCart = cartFromApi
                    } else if (cartMineFromApi) {
                        console.log("Setting cart from mine")
                        finalCart = cartMineFromApi
                        if (!cartIdStorage || cartMineFromApi.id !== cartIdStorage) {
                            setCartIdStorage(cartMineFromApi.id)
                        }
                    } else if (cartFromApi) {
                        console.log("Setting cart via id")
                        finalCart = cartFromApi
                    } else {
                        createNewCart()
                        console.log("Setting a new cart")
                        finalCart = {item_lines: []}
                    }
                    // Partnership agreement override
                    if (agreeementPartnershipEnabled && userProfile?.center?.partnership_agreement) {
                        console.log('OVERRIDING WITH PARTNERSHIP AGREEMENT', userProfile.center.partnership_agreement.code)
                        finalCart.agreement = userProfile.center.partnership_agreement
                    }
                    setCartForUi(prepareCartOrOrderForUi(finalCart, productsOverride))
                    setFirstCartLoaded(true)
                }
            }
        },
        [dependenciesAreReady, isFetchingCartMineFromApi, isLoggedUserFromQuery, user, partnerFromQuery])

    useEffect(() => {
        if (initialProduct && products) {
            const finalCart = {item_lines: [mapProductToItemLine(initialProduct)]}
            setCartForUi(prepareCartOrOrderForUi(finalCart, products))
        }
    }, [initialProduct, products])

    /*-- FUNCTIONS --------------------------------------------------------------------------*/

    const createNewCart = () => {
        createCartMutation.reset()
        createCartMutation.mutate(null, {
            onSuccess: (data) => {
                setCartIdStorage(data.id)
            },
            onError: (error) => {
                toast.error("Errore in fase di preparazione del carrello")
                throw new Error(error)
            },
        })
    }

    const saveCart = () => {
        const cartForPut = prepareCartOrOrderForPut(cartForUi)
        updateCartMutation.mutate({cartId: cartId, data: cartForPut}, {
            onError: (error) => {
                toast.error("Errore in fase di salvataggio del carrello")
                throw new Error(error)
            },
        })
    }


    const handleSubmit = (values) => {
        updateCart(values, true)
        if (user) {
            router.push('/landing/login')
        } else {
            router.push('/landing/loginOrsignup')
        }
    }
    const updateCart = (values, additionalFieldsWereValidated) => {
        const newCartForUi = _.cloneDeep(cartForUi)
        for (const item_line of newCartForUi.item_lines) {
            for (const asset of item_line.assets) {
                const value_id = `${ item_line.id }_${ asset.id }`
                asset.value = (values[value_id] === "") ? null : values[value_id]
            }
        }

        if (additionalFieldsWereValidated) { // It was validated by the submit in Configurator, before this function was called
            newCartForUi.agreement_code = values[AGREEMENT_CODE_FIELD_NAME]
        }

        if (!_.isEqual(newCartForUi, cartForUi)) {
            setCartForUi(newCartForUi)
        }
    }


    /*-- FUNCTIONS --------------------------------------------------------------------------*/

    if (role && role.toLowerCase() !== 'customer') {
        return <UnAuthorized/>
    } else if (isIdleProducts || isLoadingProducts || (cartQueryEnabled && isLoadingCartFromApi) || (cartMineQueryEnabled && isLoadingCartMineFromApi)) {
        return <Loading/>
    } else if (isErrorProducts) {
        console.error(errorProducts)
        return <AppError/>
    }
    return (
        <WrapperComponent>
            <Row className="justify-content-center">
                <Col xs={ 12 } md={ 10 } lg={ 8 } xl={ 6 } className="text-center py-6">
                    <p className='text-muted'>PASSO 1 DI 3</p>
                    <h1 className="mb-3">Composizione ordine</h1>
                    <p className="mb-5 text-muted">
                        Scegli e configura i servizi che vuoi acquistare.<br/>
                        Puoi aggiungere quanti servizi vuoi.
                    </p>
                </Col>
            </Row>
            <Configurator
                cartOrOrderForUi={ cartForUi }
                setCartOrOrderForUi={ setCartForUi }
                dependenciesAreReady={ dependenciesAreReady }
                handleSubmit={ handleSubmit }
                submitBtnText={ "Procedi all'acquisto" }
                isUpdating={ updateCartMutation.isLoading }
                canEditAgreementCode={srcFromQuery?.toLowerCase() !== 'agreement-link'}
            />
        </WrapperComponent>
    )
}

export default Index